import axios from "axios";
import { CONFIGURATIONS } from "./envConfig";
import { errorMessages } from "../constants/frontendErrorMessages";

let injectedStore = null;
export const injectStore = (s) => {
  injectedStore = s;
};

export const api = axios.create({
  baseURL: `${CONFIGURATIONS.API_BASE_URL}/`,
  withCredentials: true, // Ensures cookies are included in all requests
});

// NOTE: Store pending requests with their cancel tokens
let pendingRequests = [];

// NOTE: Listen for auth state changes
window.addEventListener("auth-state-changed", () => {
  // Cancel any pending requests
  pendingRequests.forEach((request) => {
    try {
      request.cancel();
    } catch (err) {
      console.error("Error canceling request:", err);
    }
  });

  // Clear the pending requests array
  pendingRequests = [];

  window.location.reload();
});

// NOTE: Request interceptor
api.interceptors.request.use(
  (config) => {
    // Create a cancel token for this request
    const source = axios.CancelToken.source();
    config.cancelToken = source.token;

    // Track this request (only for non-auth requests)
    // Skip tracking for auth-related endpoints like login/logout
    const isAuthEndpoint = config.url.includes("/auth/", "/user/inquiry");

    if (!isAuthEndpoint) {
      pendingRequests.push({
        url: config.url,
        cancel: () =>
          source.cancel("Request canceled due to auth state change"),
      });
    }

    // Add auth token
    if (!config?.voidAuth) {
      const token = localStorage.getItem("token");
      if (token) {
        config.headers["Authorization"] = `Bearer ${token}`;
      }
    } else {
      delete config.voidAuth;
    }

    return config;
  },

  (error) => {
    console.error("Request setup error:", error);
    return Promise.reject(error);
  },
);

// NOTE: response inteceptor
api.interceptors.response.use(
  // NOTE: in success
  async (response) => {
    // Remove the request from pending requests list
    const index = pendingRequests.findIndex(
      (req) => req.url === response.config.url,
    );
    if (index > -1) {
      pendingRequests.splice(index, 1);
    }
    return response;
  },

  // NOTE: in failure
  async (error) => {
    // Remove from pending even on error
    if (error.config) {
      const index = pendingRequests.findIndex(
        (req) => req.url === error.config.url,
      );
      if (index > -1) {
        pendingRequests.splice(index, 1);
      }
    }

    // Check if this error was caused by cancellation due to auth change
    if (axios.isCancel(error)) {
      console.log("Request canceled:", error.message);
      return Promise.reject(error);
    }

    // Handle server down / network errors (backend unreachable)
    if (!error.response) {
      // This means the server is completely unreachable (no response received)
      console.error("Server is down or unreachable:", error.message);

      // Check if it's a network error (server down)
      if (
        error.code === "ERR_NETWORK" ||
        error.message.includes("Network Error")
      ) {
        // Don't try to refresh or retry - navigate directly to server error page
        window.location.href = "/server-error";
        return Promise.reject(error);
      }

      // Other connection errors
      error.message =
        "Unable to connect to server. Please check your internet connection.";
      error.code = "503";
      return Promise.reject(error);
    }

    // Handle 401 Unauthorized errors
    if (error.response?.status === 401) {
      // Check if this is an auth endpoint (like login)
      const isAuthEndpoint = error.config.url.includes("auth/");

      if (!isAuthEndpoint) {
        // For non-auth endpoints, handle with modal instead of reload

        // Don't clear localStorage here, just show the modal
        const authAction = require("../store/slices/authSlice");
        // Check if we're already on a protected route
        const currentPath = window.location.pathname;
        const isProtectedRoute = !["/login", "/onboarding", "/signin"].some(
          (route) => currentPath.includes(route),
        );

        if (isProtectedRoute) {
          // Only open modal if we're on a protected route
          injectedStore.dispatch(authAction.openSignInModal());
        } else {
          // For public routes, just log out
          injectedStore.dispatch(authAction.handleSessionExpiration());
        }

        // Update error details
        error.message = "Session expired. Please sign in again.";
        error.code = "401";
        error.name = "AuthError";
      } else {
        // For auth endpoints themselves (like failed login attempts),
        // just pass through the error normally
        if (error.response) {
          const data = error.response.data;
          error.message = data?.message || errorMessages.ERROR_OCCURRED;
          error.code =
            `${error.response?.httpStatus}` ||
            `${error.response?.status}` ||
            "500";
          error.name = data?.statusCode || "Axios Error";
        }
      }
    }
    // Handle 500 Internal Server Error from backend
    else if (error.response?.status === 500) {
      console.error("Internal Server Error:", error.response.data);

      // Check if global error handling should be skipped for this request
      if (error.config?.skipGlobalErrorHandling) {
        return Promise.reject(error);
      }

      // Navigate to server error page for 500 errors
      window.location.href = "/server-error";
      return Promise.reject(error);
    }
    // Handle other HTTP errors
    else if (error.response) {
      // Existing response with error status
      const data = error.response.data;
      error.message = data?.message || errorMessages.ERROR_OCCURRED;
      error.code =
        `${error.response?.httpStatus}` || `${error.response?.status}` || "500";
      error.name = data?.statusCode || "Axios Error";
    } else {
      // Error setting up the request
      error.message = error.message || errorMessages.UNKNOWN_ERROR_OCCURRED;
      error.code = "500";
    }

    return Promise.reject(error);
  },
);
